import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './FirstCallModal.module.css'
import AnimatedButton from '../AnimatedButton/AnimatedButton'
import NewModal from '../NewModal/NewModal'
import { getAgentCalls } from '../../Store/apiStore'

/*
 * The once-per-24h marker lives in a COOKIE, not localStorage.
 *
 * It used to be `localStorage.firstCallModal.lastShown.<agentId>`, but the
 * logout handler calls `localStorage.clear()` (FastAgentDetails ~1035, and the
 * same in Start / Dashboard / Onboarding / AgentDetail). That wiped the marker,
 * so the next login read 0, skipped the window entirely, and the celebration
 * reappeared — the reported "shows on every login, well inside 24h".
 *
 * Cookies survive localStorage.clear(), which is exactly why the calendar nudge
 * originally used one too. Still per-device: for a cross-device rule this would
 * have to be server-side, like the caught-banner dismissal.
 */
const FIRST_CALL_COOKIE_PREFIX = "rexpt_first_call_shown_";

const firstCallCookieName = (agentId) =>
    `${FIRST_CALL_COOKIE_PREFIX}${encodeURIComponent(String(agentId || "unknown"))}`;

const readCookie = (name) => {
    if (typeof document === "undefined") return "";
    const match = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : "";
};

const writeCookie = (name, value, maxAgeSeconds) => {
    if (typeof document === "undefined") return;
    document.cookie =
        `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
};

/**
 * First-call celebration modal — shown once the agent handles its very first
 * call (callCount === 1). Fetches the latest call live from the call-history
 * API and shows its number / duration / lead details. No close (×) button;
 * dismiss via "View Call Details" or the "Skip for Now" link.
 */
const FirstCallModal = ({
    callCount = 0,
    agentId,
    agentName = "Sophia",
    // Host passes false until the intro tour finishes (tour goes first).
    onboardingReady = true,
    onViewCallDetails,
}) => {
    const navigate = useNavigate();
    const [firstCallDismissed, setFirstCallDismissed] = useState(false);
    // The actual first call, fetched from the call-history API.
    const [firstCall, setFirstCall] = useState(null);
    // Once-per-24h gate (scoped per agent) so the celebration shows a single
    // time even if the page is revisited while callCount is still 1.
    const [eligible, setEligible] = useState(false);

    // "View Call Details" → open the actual first call's detail page (same
    // route + state shape AgentAnalysis uses). Falls back to the host-provided
    // handler, then to just dismissing if the call isn't loaded yet.
    const handleViewCallDetails = () => {
        if (onViewCallDetails) { onViewCallDetails(); return; }
        if (firstCall?.call_id) {
            navigate(`/call-details/${firstCall.call_id}`, {
                state: {
                    agentId: firstCall.agent_id || agentId,
                    start_timestamp: firstCall.start_timestamp,
                },
            });
            return;
        }
        setFirstCallDismissed(true);
    };

    const hasFirstCall = Number(callCount) === 1;

    // Show at most once per rolling 24h window, scoped to this agent. Recorded
    // on first display (not only on dismiss) so navigating back doesn't reopen
    // it. When callCount > 1, hasFirstCall is false → it never shows.
    useEffect(() => {
        if (!agentId || !hasFirstCall || !onboardingReady) {
            setEligible(false);
            return;
        }
        const cookieName = firstCallCookieName(agentId);
        const DAY_MS = 24 * 60 * 60 * 1000;
        try {
            const last = Number(readCookie(cookieName) || 0);
            if (last && Date.now() - last < DAY_MS) {
                setEligible(false);
                return;
            }
            // maxAge just over the window — the cookie expires on its own, so a
            // stale value can never outlive the throttle it represents.
            writeCookie(cookieName, String(Date.now()), 25 * 60 * 60);
        } catch (_e) {
            // storage unavailable — still allow the modal for this mount
        }
        setEligible(true);
    }, [agentId, hasFirstCall, onboardingReady]);

    // ms → "Xm Ys" (e.g. 155000 → "2m 35s"). Returns null when unavailable.
    const formatDuration = (ms) => {
        if (ms == null || isNaN(ms)) return null;
        const totalSec = Math.floor(Number(ms) / 1000);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        return `${m}m ${s}s`;
    };

    // Fetch the latest call once we know the agent has exactly one call.
    useEffect(() => {
        if (!hasFirstCall || !agentId || firstCall) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getAgentCalls(agentId);
                // Response shape tolerance: array, {calls}, or {data}.
                const list = Array.isArray(res) ? res : (res?.calls || res?.data || []);
                if (!Array.isArray(list) || list.length === 0) return;
                // Most recent call first.
                const latest = [...list].sort(
                    (a, b) => Number(b?.start_timestamp || 0) - Number(a?.start_timestamp || 0)
                )[0];
                if (!cancelled) setFirstCall(latest || null);
            } catch (err) {
                console.warn("[FirstCallModal] first-call fetch failed:", err?.message || err);
            }
        })();
        return () => { cancelled = true; };
    }, [hasFirstCall, agentId, firstCall]);

    // Derive display values from the fetched call.
    const analysis = firstCall?.custom_analysis_data || firstCall?.call_analysis?.custom_analysis_data;
    const firstCallNumber =
        firstCall?.call_type === "phone_call"
            ? firstCall?.from_number
            : firstCall?.call_type === "web_call"
                ? "Web Call"
                : firstCall?.call_type === "api_chat"
                    ? "Chat"
                    : firstCall?.from_number;
    const firstCallDuration = formatDuration(firstCall?.duration_ms);
    const firstCallInterest = analysis?.lead_type;
    const firstCallLeadCaptured = !!(analysis?.lead_type || analysis?.message_taken);

    return (
        <NewModal
            isOpen={eligible && hasFirstCall && !firstCallDismissed && onboardingReady}
            onClose={() => { }}
            title={null}
            height="auto"
            showCloseBtn={false}
            bodyMaxHeight="90vh"
        >
            <div className={styles.firstCallModal}>
                <div className={styles.firstCallHead}>
                    <div className={styles.firstCallCheck}>
                        {/* expanding soft waves */}
                        <span className={styles.checkWave} />
                        <span className={styles.checkWave} />
                        <span className={styles.checkWave} />

                        {/* colorful confetti */}
                        <span className={`${styles.confetti} ${styles.confetti1}`} />
                        <span className={`${styles.confetti} ${styles.confetti2}`} />
                        <span className={`${styles.confetti} ${styles.confetti3}`} />
                        <span className={`${styles.confetti} ${styles.confetti4}`} />
                        <span className={`${styles.confetti} ${styles.confetti5}`} />
                        <span className={`${styles.confetti} ${styles.confetti6}`} />
                        <span className={`${styles.confetti} ${styles.confetti7}`} />
                        <span className={`${styles.confetti} ${styles.confetti8}`} />

                        {/* green check circle */}
                        <span className={styles.checkCircle}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                                <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </div>
                    <div className={styles.firstCallHeadText}>
                        <h2 className={styles.firstCallTitle}>Your AI handled its first call!</h2>
                        <p className={styles.firstCallSub}>Great start, {agentName}.</p>
                    </div>
                </div>

                <p className={styles.firstCallDesc}>You're already protecting your business.</p>

                <div className={styles.firstCallCard}>
                    <h3 className={styles.firstCallCardTitle}>
                        What <span className={styles.firstCallUnderline}>happened?</span>
                    </h3>

                    <div className={styles.firstCallRow}>
                        <span className={styles.firstCallRowIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M5 4l3 1 1.5 3-2 1.5a11 11 0 0 0 6 6l1.5-2 3 1.5L19 19a2 2 0 0 1-2 2 16 16 0 0 1-15-15 2 2 0 0 1 2-2z" stroke="#6524EB" strokeWidth="1.7" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <span className={styles.firstCallRowText}>
                            Call from: <span className={styles.firstCallValue}>{firstCallNumber || "Unknown"}</span>
                        </span>
                    </div>

                    {firstCallDuration && (
                        <div className={styles.firstCallRow}>
                            <span className={styles.firstCallRowIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="8.5" stroke="#6524EB" strokeWidth="1.7" />
                                    <path d="M12 7.5V12l3 1.8" stroke="#6524EB" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span className={styles.firstCallRowText}>
                                Duration: <span className={styles.firstCallValue}>{firstCallDuration}</span>
                            </span>
                        </div>
                    )}

                    {firstCallLeadCaptured && (
                        <div className={styles.firstCallRow}>
                            <span className={styles.firstCallRowCheck}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span className={styles.firstCallRowText}>
                                <span className={styles.firstCallValue}>Lead</span> Captured
                            </span>
                        </div>
                    )}

                    {firstCallInterest && (
                        <div className={styles.firstCallRow}>
                            <span className={styles.firstCallRowCheck}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span className={styles.firstCallRowText}>
                                Interested in: <span className={styles.firstCallValue}>{firstCallInterest}</span>
                            </span>
                        </div>
                    )}
                </div>

                <AnimatedButton
                    label="View Call Details"
                    onClick={handleViewCallDetails}
                    positionType="relative"
                    width="100%"
                    bottom="0"
                />

                <button
                    type="button"
                    className={styles.firstCallSkip}
                    onClick={() => setFirstCallDismissed(true)}
                >
                    Skip for Now
                </button>
            </div>
        </NewModal>
    )
}

export default FirstCallModal
